import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { getToken } from "@/utils/auth";
import ProtectedRoute from "@/components/ProtectedRoute";
import RoleGate from "@/components/RoleGate";
import Lucide from "@/components/Base/Lucide";
import { FormInput, FormCheck, FormSwitch, FormSelect } from "@/components/Base/Form"; // CHANGED: Added FormSwitch and FormSelect
import Button from "@/components/Base/Button";
import Table from "@/components/Base/Table";
import Pagination from "@/components/Base/Pagination";
import Alert from "@/components/Base/Alert";

type Outlet = {
    id: string;
    name: string;
    code?: string;
    outletGroup?: string;
    outletCategory?: string;
    stateId?: string;
    address?: string;
    city?: string;
    contactPIC?: string;
    status: boolean;
};

type TeamOutlet = {
    id: string;
    teamId: string;
    outletId: string;
    status: boolean;
    createdAt: string;
    updatedAt: string;
};

// NEW: Type for team route data
type TeamRoute = {
    id: string;
    teamId: string;
    dayOfWeek: number;
    outlets: {
        id: string;
        outletId: string;
        weight?: number;
    }[];
};

// NEW: Day names constant
const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export default function AssignOutlet() {
    const { id: teamId } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // State management
    const [outlets, setOutlets] = useState<Outlet[]>([]);
    const [selectedOutlets, setSelectedOutlets] = useState<Set<string>>(new Set());
    const [existingOutlets, setExistingOutlets] = useState<Set<string>>(new Set());
    const [originalOutlets, setOriginalOutlets] = useState<Set<string>>(new Set());
    const [loading, setLoading] = useState(true);
    const [assigning, setAssigning] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [sortField, setSortField] = useState<keyof Outlet | null>(null);
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const [states, setStates] = useState<Array<{ id: string; name: string }>>([]);
    const [regions, setRegions] = useState<Array<{ id: string; name: string; states: Array<{ stateId: string }> }>>([]);

    // NEW: State for daily route toggle
    const [enableDailyRoute, setEnableDailyRoute] = useState(false);

    // NEW: State for day selections - Map<outletId, Set<dayOfWeek>>
    const [daySelections, setDaySelections] = useState<Map<string, Set<number>>>(new Map());

    // NEW: State for original day selections to track changes
    const [originalDaySelections, setOriginalDaySelections] = useState<Map<string, Set<number>>>(new Map());

    // Get companyId and projectId from URL params
    const companyId = searchParams.get("companyId");
    const projectId = searchParams.get("projectId"); // NEW: Get projectId

    // Load outlets and existing team outlets
    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);

                // Load states first
                const statesRes = await fetch(
                    `${import.meta.env.VITE_API_URL}/states`,
                    {
                        headers: { Authorization: `Bearer ${getToken()}` }
                    }
                );
                const statesData = await statesRes.json();
                setStates(Array.isArray(statesData?.data) ? statesData.data : []);

                // Fetch ALL outlets by paginating through all pages
                let allOutlets: any[] = [];
                let currentPage = 1;
                let hasMorePages = true;
                const pageSize = 1000; // Fetch 1000 at a time

                while (hasMorePages) {
                    const queryParams = new URLSearchParams();
                    if (companyId) queryParams.append("companyId", companyId);
                    queryParams.append("pageSize", pageSize.toString());
                    queryParams.append("page", currentPage.toString());

                    const outletsRes = await fetch(
                        `${import.meta.env.VITE_API_URL}/outlets?${queryParams.toString()}`,
                        {
                            headers: { Authorization: `Bearer ${getToken()}` }
                        }
                    );
                    const outletsData = await outletsRes.json();
                    const pageOutlets = Array.isArray(outletsData?.data) ? outletsData.data : [];
                    
                    if (pageOutlets.length > 0) {
                        allOutlets = [...allOutlets, ...pageOutlets];
                        const totalPages = outletsData.totalPages || 1;
                        hasMorePages = currentPage < totalPages;
                        currentPage++;
                    } else {
                        hasMorePages = false;
                    }
                }

                setOutlets(allOutlets);

                // Load regions with states - fetch all regions with pagination
                let allRegions: any[] = [];
                let regionPage = 1;
                let hasMoreRegionPages = true;
                const regionPageSize = 1000;

                while (hasMoreRegionPages) {
                    const queryParams = new URLSearchParams();
                    if (companyId) queryParams.append("companyId", companyId);
                    queryParams.append("pageSize", regionPageSize.toString());
                    queryParams.append("page", regionPage.toString());

                    const regionsRes = await fetch(
                        `${import.meta.env.VITE_API_URL}/regions?${queryParams.toString()}`,
                        {
                            headers: { Authorization: `Bearer ${getToken()}` }
                        }
                    );
                    const regionsData = await regionsRes.json();
                    const pageRegions = Array.isArray(regionsData?.data) ? regionsData.data : [];
                    
                    if (pageRegions.length > 0) {
                        allRegions = [...allRegions, ...pageRegions];
                        const totalPages = regionsData.totalPages || 1;
                        hasMoreRegionPages = regionPage < totalPages;
                        regionPage++;
                    } else {
                        hasMoreRegionPages = false;
                    }
                }

                // Fetch states for each region to build the mapping
                const regionsWithStates = await Promise.all(
                    allRegions.map(async (region) => {
                        try {
                            const regionRes = await fetch(
                                `${import.meta.env.VITE_API_URL}/regions/${region.id}`,
                                {
                                    headers: { Authorization: `Bearer ${getToken()}` }
                                }
                            );
                            const regionData = await regionRes.json();
                            return {
                                id: region.id,
                                name: region.name,
                                states: Array.isArray(regionData?.states) 
                                    ? regionData.states.map((s: any) => ({ stateId: s.id }))
                                    : []
                            };
                        } catch (error) {
                            console.error(`Failed to fetch region ${region.id}:`, error);
                            return {
                                id: region.id,
                                name: region.name,
                                states: []
                            };
                        }
                    })
                );

                setRegions(regionsWithStates);

                // Load existing team outlets (regular mode)
                const teamOutletsRes = await fetch(
                    `${import.meta.env.VITE_API_URL}/teams/${teamId}/outlets`,
                    {
                        headers: { Authorization: `Bearer ${getToken()}` }
                    }
                );
                const teamOutletsData = await teamOutletsRes.json();
                const outletIds: Set<string> = new Set(teamOutletsData?.data?.map((teamOutlet: TeamOutlet) => teamOutlet.outletId) || []);
                setExistingOutlets(outletIds);
                setOriginalOutlets(outletIds);
                setSelectedOutlets(outletIds);

                // NEW: Load team to check enableTeamRoute status
                const teamRes = await fetch(
                    `${import.meta.env.VITE_API_URL}/teams/${teamId}`,
                    {
                        headers: { Authorization: `Bearer ${getToken()}` }
                    }
                );
                const teamData = await teamRes.json();
                const isRouteEnabled = teamData?.enableTeamRoute || false;
                setEnableDailyRoute(isRouteEnabled);

                // NEW: Load existing team routes if route is enabled
                if (isRouteEnabled) {
                    await loadTeamRoutes();
                }

            } catch (error) {
                console.error("Failed to load data:", error);
                setMessage({ type: 'error', text: 'Failed to load data' });
            } finally {
                setLoading(false);
            }
        };

        if (teamId) {
            loadData();
        }
    }, [teamId, companyId]);

    // NEW: Function to load team routes
    const loadTeamRoutes = async () => {
        try {
            const routesRes = await fetch(
                `${import.meta.env.VITE_API_URL}/teams/${teamId}/routes`,
                {
                    headers: { Authorization: `Bearer ${getToken()}` }
                }
            );
            const routesData = await routesRes.json();
            const routes: TeamRoute[] = routesData?.data || [];

            // Build day selections map from routes
            const newDaySelections = new Map<string, Set<number>>();
            const allRouteOutletIds = new Set<string>();

            routes.forEach(route => {
                route.outlets.forEach(outlet => {
                    allRouteOutletIds.add(outlet.outletId);

                    if (!newDaySelections.has(outlet.outletId)) {
                        newDaySelections.set(outlet.outletId, new Set());
                    }
                    newDaySelections.get(outlet.outletId)!.add(route.dayOfWeek);
                });
            });

            setDaySelections(newDaySelections);
            setOriginalDaySelections(new Map(Array.from(newDaySelections.entries()).map(
                ([key, value]) => [key, new Set(value)]
            )));

            // Update selected outlets to match routes
            setSelectedOutlets(allRouteOutletIds);
            setExistingOutlets(allRouteOutletIds);
            setOriginalOutlets(allRouteOutletIds);

        } catch (error) {
            console.error("Failed to load team routes:", error);
            setMessage({ type: 'error', text: 'Failed to load team routes' });
        }
    };

    // Handle individual outlet selection
    const handleOutletSelect = (outletId: string) => {
        setSelectedOutlets(prev => {
            const newSet = new Set(prev);
            if (newSet.has(outletId)) {
                newSet.delete(outletId);
                // NEW: Also clear day selections when outlet is deselected
                if (enableDailyRoute) {
                    setDaySelections(prevDays => {
                        const newDays = new Map(prevDays);
                        newDays.delete(outletId);
                        return newDays;
                    });
                }
            } else {
                newSet.add(outletId);
            }
            return newSet;
        });
    };

    // Handle select all
    const handleSelectAll = () => {
        const allOutletIds = new Set(outlets.map(outlet => outlet.id));
        setSelectedOutlets(allOutletIds);
        
        // NEW: In daily route mode, also select all days for all outlets
        if (enableDailyRoute) {
            const newDaySelections = new Map<string, Set<number>>();
            outlets.forEach(outlet => {
                // Select all 7 days (Mon-Sun) for each outlet
                newDaySelections.set(outlet.id, new Set([1, 2, 3, 4, 5, 6, 7]));
            });
            setDaySelections(newDaySelections);
        }
    };

    // Handle deselect all
    const handleDeselectAll = () => {
        setSelectedOutlets(new Set());
        // NEW: Clear all day selections
        if (enableDailyRoute) {
            setDaySelections(new Map());
        }
    };

    // NEW: Handle day selection for an outlet
    const handleDaySelect = (outletId: string, dayOfWeek: number) => {
        setDaySelections(prev => {
            const newMap = new Map(prev);
            if (!newMap.has(outletId)) {
                newMap.set(outletId, new Set());
            }
            const days = newMap.get(outletId)!;

            if (days.has(dayOfWeek)) {
                days.delete(dayOfWeek);
                if (days.size === 0) {
                    newMap.delete(outletId);
                    // ADDED: Remove outlet from selectedOutlets if no days are selected
                    setSelectedOutlets(prevSelected => {
                        const newSelected = new Set(prevSelected);
                        newSelected.delete(outletId);
                        return newSelected;
                    });
                }
            } else {
                days.add(dayOfWeek);
                // ADDED: Add outlet to selectedOutlets when any day is selected
                setSelectedOutlets(prevSelected => {
                    const newSelected = new Set(prevSelected);
                    newSelected.add(outletId);
                    return newSelected;
                });
            }

            return newMap;
        });
    };

    // NEW: Check if a day is selected for an outlet
    const isDaySelected = (outletId: string, dayOfWeek: number): boolean => {
        return daySelections.get(outletId)?.has(dayOfWeek) || false;
    };

    // Handle column sorting
    const handleSort = (field: keyof Outlet) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    // MODIFIED: Assign/remove selected outlets to/from team (handles both modes)
    const handleAssignOutlets = async () => {
        try {
            setAssigning(true);
            setMessage(null);

            const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';
            const token = getToken();

            if (enableDailyRoute) {
                // NEW: Daily route mode - update routes
                // Build routes data structure
                const routesData: { dayOfWeek: number; outletIds: string[] }[] = [];

                for (let day = 1; day <= 7; day++) {
                    const outletsForDay = Array.from(selectedOutlets).filter(outletId =>
                        isDaySelected(outletId, day)
                    );
                    routesData.push({
                        dayOfWeek: day,
                        outletIds: outletsForDay
                    });
                }

                // Send update to backend
                await fetch(`${apiUrl}/teams/${teamId}/routes`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        routes: routesData,
                        enableTeamRoute: true  // Keep this to ensure consistency
                    })
                });

                setMessage({ type: 'success', text: 'Team daily routes updated successfully' });

                // Refresh data
                await loadTeamRoutes();

            } else {
                // EXISTING: Regular mode - update team outlets
                const outletsToAdd = Array.from(selectedOutlets).filter(outletId => !originalOutlets.has(outletId));
                const outletsToRemove = Array.from(originalOutlets).filter(outletId => !selectedOutlets.has(outletId));

                if (outletsToAdd.length === 0 && outletsToRemove.length === 0) {
                    setMessage({ type: 'error', text: 'No changes to make' });
                    setAssigning(false);
                    return;
                }

                const promises = [];

                // Add new team outlets
                if (outletsToAdd.length > 0) {
                    const addPromises = outletsToAdd.map(outletId =>
                        fetch(`${apiUrl}/teams/${teamId}/outlets`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                            },
                            body: JSON.stringify({ outletId, status: true })
                        })
                    );
                    promises.push(...addPromises);
                }

                // Remove team outlets
                if (outletsToRemove.length > 0) {
                    const removePromises = outletsToRemove.map(outletId =>
                        fetch(`${apiUrl}/teams/${teamId}/outlets/${outletId}`, {
                            method: 'DELETE',
                            headers: {
                                'Authorization': `Bearer ${token}`
                            }
                        })
                    );
                    promises.push(...removePromises);
                }

                await Promise.all(promises);

                const actionText = outletsToAdd.length > 0 && outletsToRemove.length > 0
                    ? `Successfully updated team: added ${outletsToAdd.length} outlet(s), removed ${outletsToRemove.length} outlet(s)`
                    : outletsToAdd.length > 0
                        ? `Successfully added ${outletsToAdd.length} outlet(s) to the team`
                        : `Successfully removed ${outletsToRemove.length} outlet(s) from the team`;

                setMessage({ type: 'success', text: actionText });

                // Refresh existing outlets
                const teamOutletsRes = await fetch(
                    `${apiUrl}/teams/${teamId}/outlets`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                const teamOutletsData = await teamOutletsRes.json();
                const outletIds: Set<string> = new Set(teamOutletsData?.data?.map((teamOutlet: TeamOutlet) => teamOutlet.outletId) || []);
                setExistingOutlets(outletIds);
                setOriginalOutlets(outletIds);
                setSelectedOutlets(outletIds);
            }

        } catch (error) {
            console.error("Failed to update team outlets:", error);
            setMessage({ type: 'error', text: 'Failed to update team outlets' });
        } finally {
            setAssigning(false);
        }
    };

    // NEW: Handle toggle change
    const handleToggleChange = async (checked: boolean) => {
        setMessage(null); // Clear any existing messages when toggling
        
        try {
            const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';
            const token = getToken();

            // UPDATED: Immediately save the toggle state to database
            await fetch(`${apiUrl}/teams/${teamId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    enableTeamRoute: checked
                })
            });

            // Update local state after successful save
            setEnableDailyRoute(checked);

            // Show success message
            setMessage({ 
                type: 'success', 
                text: checked 
                    ? 'Daily Team Route enabled' 
                    : 'Daily Team Route disabled' 
            });

            // Auto-hide message after 2 seconds
            setTimeout(() => setMessage(null), 2000);

            if (checked) {
                // Switching to daily route mode - load routes
                setLoading(true);
                await loadTeamRoutes();
                setLoading(false);
            } else {
                // Switching to regular mode - load regular outlets
                setLoading(true);
                const teamOutletsRes = await fetch(
                    `${apiUrl}/teams/${teamId}/outlets`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                const teamOutletsData = await teamOutletsRes.json();
                const outletIds: Set<string> = new Set(teamOutletsData?.data?.map((teamOutlet: TeamOutlet) => teamOutlet.outletId) || []);
                setExistingOutlets(outletIds);
                setOriginalOutlets(outletIds);
                setSelectedOutlets(outletIds);
                setLoading(false);
            }
        } catch (error) {
            console.error("Failed to update toggle state:", error);
            setMessage({ type: 'error', text: 'Failed to update toggle state' });
            // Don't change the toggle state if the API call failed
        }
    };

    // Create mappings for state and region names
    const stateIdToNameMap = new Map<string, string>();
    states.forEach(state => {
        if (state.id && state.name) {
            stateIdToNameMap.set(state.id, state.name);
        }
    });

    const stateIdToRegionMap = new Map<string, string>();
    regions.forEach(region => {
        if (region.states && Array.isArray(region.states)) {
            region.states.forEach((rs: { stateId: string }) => {
                if (rs.stateId) {
                    stateIdToRegionMap.set(rs.stateId, region.name);
                }
            });
        }
    });

    // Helper function to get state name
    const getStateName = (stateId?: string): string => {
        if (!stateId) return "—";
        return stateIdToNameMap.get(stateId) || "—";
    };

    // Helper function to get region name
    const getRegionName = (stateId?: string): string => {
        if (!stateId) return "—";
        return stateIdToRegionMap.get(stateId) || "—";
    };

    // Filter and sort outlets
    const filteredAndSortedOutlets = outlets
        .filter(outlet =>
            outlet.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            outlet.outletGroup?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            outlet.outletCategory?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            getStateName(outlet.stateId).toLowerCase().includes(searchQuery.toLowerCase()) ||
            getRegionName(outlet.stateId).toLowerCase().includes(searchQuery.toLowerCase())
        )
        .sort((a, b) => {
            if (!sortField) return 0;

            let aValue: any;
            let bValue: any;

            // Handle special cases for stateId (convert to state name for sorting)
            if (sortField === 'stateId') {
                aValue = getStateName(a.stateId);
                bValue = getStateName(b.stateId);
            } else {
                aValue = a[sortField];
                bValue = b[sortField];
            }

            const aStr = String(aValue || '');
            const bStr = String(bValue || '');

            if (sortDirection === 'asc') {
                return aStr.localeCompare(bStr);
            } else {
                return bStr.localeCompare(aStr);
            }
        });

    // Calculate pagination
    const totalFiltered = filteredAndSortedOutlets.length;
    const calculatedTotalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedOutlets = filteredAndSortedOutlets.slice(startIndex, endIndex);

    // Update totalPages and total when filtered results change
    React.useEffect(() => {
        setTotalPages(calculatedTotalPages);
        setTotal(totalFiltered);
        // Reset to page 1 if current page is beyond available pages
        if (currentPage > calculatedTotalPages && calculatedTotalPages > 0) {
            setCurrentPage(1);
        }
    }, [totalFiltered, pageSize, calculatedTotalPages]);

    // Pagination handlers
    const handleFirstPage = () => setCurrentPage(1);
    const handlePrevPage = () => {
        if (currentPage > 1) setCurrentPage(currentPage - 1);
    };
    const handleNextPage = () => {
        if (currentPage < totalPages) setCurrentPage(currentPage + 1);
    };
    const handleLastPage = () => setCurrentPage(totalPages);
    const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setPageSize(Number(e.target.value));
        setCurrentPage(1);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <Lucide icon="Loader2" className="w-8 h-8 animate-spin mx-auto mb-2" />
                    <div>Loading outlets...</div>
                </div>
            </div>
        );
    }

    if (refreshing) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <Lucide icon="Loader2" className="w-8 h-8 animate-spin mx-auto mb-2" />
                    <div>Updating team outlets...</div>
                </div>
            </div>
        );
    }

    return (
        <ProtectedRoute>
            <RoleGate allow={["SUPERADMIN", "ADMIN"]}>
                <div className="grid grid-cols-12 gap-y-10 gap-x-6 max-w-7xl mx-auto">
                    <div className="col-span-12">
                        {/* Header */}
                        <div className="flex flex-col mt-4 md:mt-0 md:h-10 gap-y-3 md:items-center md:flex-row">
                            <div className="text-base font-medium group-[.mode--light]:text-white">
                                Assign Outlets to Team
                            </div>
                            <div className="flex flex-col sm:flex-row gap-x-3 gap-y-2 md:ml-auto">
                                <Button
                                    variant="outline-secondary"
                                    onClick={handleSelectAll}
                                    disabled={assigning}
                                >
                                    <Lucide icon="CheckSquare" className="stroke-[1.3] w-4 h-4 mr-2" />
                                    Select All
                                </Button>
                                <Button
                                    variant="outline-secondary"
                                    onClick={handleDeselectAll}
                                    disabled={assigning}
                                >
                                    <Lucide icon="Square" className="stroke-[1.3] w-4 h-4 mr-2" />
                                    Deselect All
                                </Button>
                            </div>
                        </div>

                        {/* Search and Table */}
                        <div className="mt-3.5">
                            <div className="flex flex-col box box--stacked max-w-full overflow-hidden">
                                <div className="flex flex-col p-5 sm:items-center sm:flex-row gap-y-2">
                                    {/* MODIFIED: Changed to flex container for search and toggle */}
                                    <div className="flex flex-col sm:flex-row gap-y-2 sm:gap-x-4 sm:items-center w-full justify-between">
                                        <div className="relative">
                                            <Lucide
                                                icon="Search"
                                                className="absolute inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3] text-slate-500"
                                            />
                                            <FormInput
                                                type="text"
                                                placeholder="Search outlets..."
                                                className="pl-9 sm:w-64 rounded-[0.5rem]"
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                            />
                                        </div>

                                        {/* NEW: Daily Route Toggle Switch */}
                                        <div className="flex items-center gap-3">
                                            <span className="text-sm font-medium text-slate-600 dark:text-slate-300">
                                                Enable Daily Team Route
                                            </span>
                                            <FormSwitch>
                                                <FormSwitch.Input
                                                    type="checkbox"
                                                    checked={enableDailyRoute}
                                                    onChange={(e) => handleToggleChange(e.target.checked)}
                                                    disabled={assigning}
                                                />
                                            </FormSwitch>
                                        </div>

                                        {/* Message moved to new row below */}
                                    </div>
                                </div>

                                {/* NEW: Message row */}
                                {message && (
                                    <div className="px-5 pb-3">
                                        <div className={`text-sm px-3 py-2 rounded-md flex items-center ${message.type === 'success'
                                            ? 'bg-success/10 text-success border border-success/20'
                                            : 'bg-danger/10 text-danger border border-danger/20'
                                            }`}>
                                            <Lucide
                                                icon={message.type === 'success' ? 'CheckCircle' : 'AlertTriangle'}
                                                className="w-4 h-4 mr-2"
                                            />
                                            {message.text}
                                        </div>
                                    </div>
                                )}

                                <div className="overflow-auto xl:overflow-visible">
                                    <Table className="border-b border-slate-200/60 text-xs">
                                        <Table.Thead className="text-xs">
                                            <Table.Tr>
                                                <Table.Td
                                                    className="cursor-pointer hover:bg-slate-50 dark:hover:bg-darkmode-700 select-none"
                                                    onClick={() => handleSort('name')}
                                                >
                                                    <div className="flex items-center gap-1 py-1.5">
                                                        Outlet Name
                                                        {sortField === 'name' && (
                                                            <Lucide
                                                                icon={sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown'}
                                                                className="w-3 h-3"
                                                            />
                                                        )}
                                                    </div>
                                                </Table.Td>
                                                <Table.Td
                                                    className="cursor-pointer hover:bg-slate-50 dark:hover:bg-darkmode-700 select-none"
                                                    onClick={() => handleSort('outletGroup')}
                                                >
                                                    <div className="flex items-center gap-1 py-1.5">
                                                        Group
                                                        {sortField === 'outletGroup' && (
                                                            <Lucide
                                                                icon={sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown'}
                                                                className="w-3 h-3"
                                                            />
                                                        )}
                                                    </div>
                                                </Table.Td>
                                                <Table.Td
                                                    className="cursor-pointer hover:bg-slate-50 dark:hover:bg-darkmode-700 select-none"
                                                    onClick={() => handleSort('outletCategory')}
                                                >
                                                    <div className="flex items-center gap-1 py-1.5">
                                                        Category
                                                        {sortField === 'outletCategory' && (
                                                            <Lucide
                                                                icon={sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown'}
                                                                className="w-3 h-3"
                                                            />
                                                        )}
                                                    </div>
                                                </Table.Td>
                                                <Table.Td
                                                    className="cursor-pointer hover:bg-slate-50 dark:hover:bg-darkmode-700 select-none"
                                                    onClick={() => handleSort('stateId')}
                                                >
                                                    <div className="flex items-center gap-1 py-1.5">
                                                        State
                                                        {sortField === 'stateId' && (
                                                            <Lucide
                                                                icon={sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown'}
                                                                className="w-3 h-3"
                                                            />
                                                        )}
                                                    </div>
                                                </Table.Td>
                                                <Table.Td className="py-1.5">
                                                    <div className="flex items-center gap-1 py-1.5">
                                                        Region
                                                    </div>
                                                </Table.Td>
                                                <Table.Td
                                                    className="cursor-pointer hover:bg-slate-50 dark:hover:bg-darkmode-700 select-none"
                                                    onClick={() => handleSort('status')}
                                                >
                                                    <div className="flex items-center gap-1 py-1.5">
                                                        Status
                                                        {sortField === 'status' && (
                                                            <Lucide
                                                                icon={sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown'}
                                                                className="w-3 h-3"
                                                            />
                                                        )}
                                                    </div>
                                                </Table.Td>

                                                {/* MODIFIED: Conditional columns based on toggle */}
                                                {enableDailyRoute ? (
                                                    // NEW: Show day columns when daily route is enabled
                                                    <>
                                                        {DAY_NAMES.map((day, index) => (
                                                            <Table.Td key={day} className="text-center py-1.5">
                                                                {day}
                                                            </Table.Td>
                                                        ))}
                                                    </>
                                                ) : (
                                                    // EXISTING: Show assign column when daily route is disabled
                                                    <Table.Td className="text-center py-1.5">Assign</Table.Td>
                                                )}
                                            </Table.Tr>
                                        </Table.Thead>
                                        <Table.Tbody>
                                            {paginatedOutlets.map((outlet) => (
                                                <Table.Tr key={outlet.id} className="[&_td]:last:border-b-0">
                                                    <Table.Td className="py-1.5 border-dashed dark:bg-darkmode-600 text-xs">
                                                        {outlet.name}
                                                    </Table.Td>
                                                    <Table.Td className="py-1.5 border-dashed dark:bg-darkmode-600 text-xs">
                                                        {outlet.outletGroup || "—"}
                                                    </Table.Td>
                                                    <Table.Td className="py-1.5 border-dashed dark:bg-darkmode-600 text-xs">
                                                        {outlet.outletCategory || "—"}
                                                    </Table.Td>
                                                    <Table.Td className="py-1.5 border-dashed dark:bg-darkmode-600 text-xs">
                                                        {getStateName(outlet.stateId)}
                                                    </Table.Td>
                                                    <Table.Td className="py-1.5 border-dashed dark:bg-darkmode-600 text-xs">
                                                        {getRegionName(outlet.stateId)}
                                                    </Table.Td>
                                                    <Table.Td className="py-1.5 border-dashed dark:bg-darkmode-600 text-xs">
                                                        <span className={`px-1.5 py-0.5 text-[10px] rounded-full ${outlet.status ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'
                                                            }`}>
                                                            {outlet.status ? "Active" : "Inactive"}
                                                        </span>
                                                    </Table.Td>

                                                    {/* MODIFIED: Conditional cells based on toggle */}
                                                    {enableDailyRoute ? (
                                                        // NEW: Show day checkboxes when daily route is enabled
                                                        <>
                                                            {DAY_NAMES.map((day, index) => {
                                                                const dayOfWeek = index + 1;
                                                                return (
                                                                    <Table.Td key={day} className="py-1.5 text-center border-dashed dark:bg-darkmode-600">
                                                                        <div className="flex justify-center items-center">
                                                                            <FormCheck>
                                                                <FormCheck.Input
                                                                    type="checkbox"
                                                                    checked={isDaySelected(outlet.id, dayOfWeek)}
                                                                    onChange={() => handleDaySelect(outlet.id, dayOfWeek)}
                                                                    disabled={assigning}
                                                                    className="scale-90"
                                                                />
                                                            </FormCheck>
                                                                        </div>
                                                                    </Table.Td>
                                                                );
                                                            })}
                                                        </>
                                                    ) : (
                                                        // EXISTING: Show assign checkbox when daily route is disabled
                                                        <Table.Td className="py-1.5 text-center border-dashed dark:bg-darkmode-600">
                                                            <div className="flex justify-center items-center">
                                                                <FormCheck>
                                                                    <FormCheck.Input
                                                                        type="checkbox"
                                                                        checked={selectedOutlets.has(outlet.id)}
                                                                        onChange={() => handleOutletSelect(outlet.id)}
                                                                        disabled={assigning}
                                                                        className="scale-90"
                                                                    />
                                                                </FormCheck>
                                                            </div>
                                                        </Table.Td>
                                                    )}
                                                </Table.Tr>
                                            ))}
                                        </Table.Tbody>
                                    </Table>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex justify-between items-center p-5 border-t border-slate-200/60">
                                    <Button
                                        variant="outline-secondary"
                                        onClick={() => navigate(-1)}
                                        disabled={assigning}
                                    >
                                        <Lucide icon="ArrowLeft" className="w-4 h-4 mr-2" />
                                        Back
                                    </Button>
                                    <div className="flex gap-3">
                                        <Button
                                            variant="outline-secondary"
                                            onClick={() => navigate(-1)}
                                            disabled={assigning}
                                        >
                                            <Lucide icon="X" className="w-4 h-4 mr-2" />
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="primary"
                                            onClick={handleAssignOutlets}
                                            disabled={assigning}
                                        >
                                            {assigning && <Lucide icon="Loader2" className="w-4 h-4 mr-2 animate-spin" />}
                                            <Lucide icon="Store" className="w-4 h-4 mr-2" />
                                            Update Team
                                        </Button>
                                    </div>
                                </div>

                                {/* Pagination */}
                                <div className="flex flex-col-reverse flex-wrap items-center p-5 flex-reverse gap-y-2 sm:flex-row">
                                    <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                                        Showing {totalFiltered > 0 ? startIndex + 1 : 0} to {Math.min(endIndex, totalFiltered)} of {totalFiltered} outlets
                                    </div>
                                    <Pagination className="flex-1 w-full mr-auto sm:w-auto">
                                        <Pagination.Link
                                            onClick={(e) => {
                                                e.preventDefault();
                                                handleFirstPage();
                                            }}
                                            className={currentPage === 1 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                        >
                                            <Lucide icon="ChevronsLeft" className="w-4 h-4" />
                                        </Pagination.Link>
                                        <Pagination.Link
                                            onClick={(e) => {
                                                e.preventDefault();
                                                handlePrevPage();
                                            }}
                                            className={currentPage === 1 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                        >
                                            <Lucide icon="ChevronLeft" className="w-4 h-4" />
                                        </Pagination.Link>
                                        {totalPages > 0 && (
                                            <>
                                                {Array.from({ length: Math.min(totalPages, 5) }).map((_, i) => {
                                                    let pageNum: number;
                                                    if (totalPages <= 5) {
                                                        pageNum = i + 1;
                                                    } else if (currentPage <= 3) {
                                                        pageNum = i + 1;
                                                    } else if (currentPage >= totalPages - 2) {
                                                        pageNum = totalPages - 4 + i;
                                                    } else {
                                                        pageNum = currentPage - 2 + i;
                                                    }
                                                    return (
                                                        <Pagination.Link
                                                            key={pageNum}
                                                            active={currentPage === pageNum}
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                setCurrentPage(pageNum);
                                                            }}
                                                            className="cursor-pointer"
                                                        >
                                                            {pageNum}
                                                        </Pagination.Link>
                                                    );
                                                })}
                                            </>
                                        )}
                                        <Pagination.Link
                                            onClick={(e) => {
                                                e.preventDefault();
                                                handleNextPage();
                                            }}
                                            className={currentPage >= totalPages ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                        >
                                            <Lucide icon="ChevronRight" className="w-4 h-4" />
                                        </Pagination.Link>
                                        <Pagination.Link
                                            onClick={(e) => {
                                                e.preventDefault();
                                                handleLastPage();
                                            }}
                                            className={currentPage >= totalPages ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                        >
                                            <Lucide icon="ChevronsRight" className="w-4 h-4" />
                                        </Pagination.Link>
                                    </Pagination>
                                    <FormSelect 
                                        className="sm:w-20 rounded-[0.5rem]"
                                        value={pageSize}
                                        onChange={handlePageSizeChange}
                                    >
                                        <option value={10}>10</option>
                                        <option value={25}>25</option>
                                        <option value={50}>50</option>
                                        <option value={100}>100</option>
                                        <option value={250}>250</option>
                                        <option value={500}>500</option>
                                    </FormSelect>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </RoleGate>
        </ProtectedRoute>
    );
}